import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Plus, Ruler, Activity } from 'lucide-react';
import InlineProfileEditor from '../InlineProfileEditor';
import TeamSelection from '../TeamSelection';
import PlayerTargets from '../PlayerTargets';
import ConsentStatus from '../ConsentStatus';
import UpcomingBookings from '../UpcomingBookings';
import AthleticAnalytics from '../AthleticAnalytics';
import Leaderboards from '../Leaderboards';
import SocialShare from '../SocialShare';
import InjuryRiskPredictor from '../InjuryRiskPredictor';
import ScoutingReport from '../ScoutingReport';
import MaturationTimeline from '../MaturationTimeline';
import AchievementBadges from '../AchievementBadges';
import WeeklyChallengeCard from '../WeeklyChallengeCard';

/**
 * Overview Tab - Main profile view with key information
 * Extracted from ChildProfile.jsx for better maintainability
 */
export default function OverviewTab({
  childId,
  child,
  onChildUpdate,
  measurements,
  athleticTests,
  testSummary,
  onJoinMeet
}) {
  const navigate = useNavigate();
  const [showAllTests, setShowAllTests] = useState(false);

  // Calculate child age for leaderboards
  const childAge = child?.date_of_birth 
    ? Math.floor((new Date() - new Date(child.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* 1. Profile Details */}
      <InlineProfileEditor 
        child={child}
        onUpdate={onChildUpdate}
      />
      
      {/* 2. Current Club / Team Selection */}
      <TeamSelection 
        childId={childId}
        childName={child ? `${child.first_name} ${child.last_name}` : ''}
        onTeamAssigned={(teamName) => onChildUpdate({ current_team_name: teamName })}
      />
      
      {/* 3. Season Targets */}
      <PlayerTargets 
        childId={childId}
        childName={child ? `${child.first_name} ${child.last_name}` : ''}
      />
      
      {/* 4. Add Measurements Button */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#02b5ff]/20 rounded-lg flex items-center justify-center">
              <Ruler className="h-5 w-5 text-[#02b5ff]" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Measurements</h3>
              <p className="text-xs text-zinc-500">
                {measurements.length > 0 
                  ? `${measurements.length} records • Last: ${new Date(measurements[measurements.length-1]?.measurement_date).toLocaleDateString()}`
                  : 'No measurements recorded yet'
                }
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/add-measurement/${childId}`)}
            className="bg-[#02b5ff] text-white"
            data-testid="add-measurement-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Measurement
          </Button>
        </div>
      </div>
      
      {/* 5. Performance Test Results */}
      {athleticTests.length > 0 && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Activity className="h-5 w-5 text-[#02b5ff]" />
              <h3 className="text-white font-semibold">Performance Test Results</h3>
              <span className="text-xs bg-[#02b5ff]/20 text-[#02b5ff] px-2 py-1 rounded-full">
                {athleticTests.length} tests
              </span>
            </div>
            {athleticTests.length > 8 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllTests(!showAllTests)}
                className="text-[#02b5ff] hover:text-white"
              >
                {showAllTests ? 'Show Less' : 'View All Details'}
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {testSummary?.test_summary?.slice(0, showAllTests ? undefined : 8).map((test) => (
              <div 
                key={test.test_name} 
                className="bg-zinc-800/50 rounded-lg p-3 hover:bg-zinc-800/70 transition-colors cursor-pointer group"
                onClick={() => {
                  const analyticsSection = document.querySelector('[data-testid="athletic-analytics"]');
                  if (analyticsSection) {
                    analyticsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                <p className="text-xs text-zinc-500 mb-1 truncate group-hover:text-[#02b5ff] transition-colors" title={test.test_name}>{test.test_name}</p>
                <p className="text-lg font-bold text-white">
                  {test.latest_value?.toFixed(2) || 'N/A'}
                  <span className="text-xs text-zinc-500 ml-1">{test.unit || ''}</span>
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-zinc-400">{test.count} test{test.count !== 1 ? 's' : ''}</p>
                  {test.latest_grade && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      test.latest_grade >= 4 ? 'bg-green-500/20 text-green-400' :
                      test.latest_grade >= 3 ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      Grade: {test.latest_grade}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#02b5ff] opacity-0 group-hover:opacity-100 transition-opacity mt-1">Click to view graphs →</p>
              </div>
            ))}
          </div>
          
          {!showAllTests && testSummary?.test_summary?.length > 8 && (
            <p className="text-center text-xs text-zinc-500 mt-3">
              +{testSummary.test_summary.length - 8} more test types — Click to expand
            </p>
          )}
        </div>
      )}
      
      {/* 6. Performance Analytics */}
      {athleticTests.length > 0 && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden p-6" data-testid="athletic-analytics">
          <AthleticAnalytics 
            childId={childId} 
            childName={`${child?.first_name} ${child?.last_name}`}
            showTeamComparison={true}
          />
        </div>
      )}
      
      {/* Maturation Timeline */}
      <MaturationTimeline childId={childId} />
      
      {/* Injury Risk Predictor */}
      <InjuryRiskPredictor childId={childId} />
      
      {/* Scouting Report */}
      <ScoutingReport childId={childId} childName={`${child?.first_name} ${child?.last_name}`} />
      
      {/* Weekly AI Challenge */}
      <WeeklyChallengeCard childId={childId} />
      
      {/* Achievement Badges & Streaks */}
      <AchievementBadges childId={childId} />
      
      {/* 7. Consent Status */}
      <ConsentStatus 
        childId={childId} 
        childName={child ? `${child.first_name} ${child.last_name}` : ''} 
        childEmail={child?.email}
        parentEmail={child?.parent_email}
      />
      
      {/* 8. Upcoming Bookings */}
      <UpcomingBookings 
        childId={childId}
        onJoinMeet={onJoinMeet}
      />

      {/* 9. Leaderboards */}
      {child && childAge && (
        <Leaderboards 
          childId={childId}
          childAge={childAge}
          isCoach={false}
        />
      )}
      
      {/* 10. Social Sharing & Achievements */}
      {child && (
        <SocialShare 
          childId={childId}
          childName={`${child.first_name} ${child.last_name}`}
        />
      )}
    </div>
  );
}
